import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    Avatar,
    IconButton,
    Divider,
    Alert,
    CircularProgress
} from '@mui/material';
import { PhotoCamera, Edit, Save } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getAdminUserProfile, updateAdminUser } from '../Services/AdminUsers/AdminUsersApis';
import { useUser } from '../context/UserContext';

// Styled components
const Input = styled('input')({
    display: 'none',
});

const Profile = () => {
    const { user, setUser, fetchUserProfile: refreshGlobalUser } = useUser();
    const [profileData, setProfileData] = useState({
        id: '',
        name: '',
        email: '',
        mobile: '',
        description: '',
        role: '',
        status: '',
        createdAt: '',
        updatedAt: '',
        image: null
    });
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imagePreview, setImagePreview] = useState(null);

    // Fetch user profile data
    const fetchProfile = useCallback(async () => {
        if (user) {
            setProfileData({
                id: user.id || '',
                name: user.name || '',
                email: user.email || '',
                mobile: user.mobile || '',
                description: user.description || '',
                role: user.role || '',
                status: user.isActive ? 'Active' : 'Inactive',
                createdAt: user.createdAt || '',
                updatedAt: user.updatedAt || '',
                image: user.image || null
            });
            setImagePreview(user.image);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await getAdminUserProfile();
            if (response && response.success) {
                const userData = response.data;
                setUser(userData); // Update global state
                setProfileData({
                    id: userData.id || '',
                    name: userData.name || '',
                    email: userData.email || '',
                    mobile: userData.mobile || '',
                    description: userData.description || '',
                    role: userData.role || '',
                    status: userData.isActive ? 'Active' : 'Inactive',
                    createdAt: userData.createdAt || '',
                    updatedAt: userData.updatedAt || '',
                    image: userData.image || null
                });
                setImagePreview(userData.image);
            }
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError('Failed to load profile data');
        } finally {
            setLoading(false);
        }
    }, [user, setUser]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        setProfileData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfileData(prev => ({
                ...prev,
                image: file
            }));
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    // Save profile changes
    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const updateData = {
                name: profileData.name,
                mobile: profileData.mobile,
                description: profileData.description,
                ...(profileData.image instanceof File && { image: profileData.image })
            };

            const response = await updateAdminUser(profileData.id, updateData);
            if (response && response.success) {
                setSuccess('Profile updated successfully');
                setEditMode(false);
                refreshGlobalUser(); // Refresh global user state to update sidebar etc.
            }
        } catch (err) {
            console.error('Error updating profile:', err);
            setError('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    // Cancel editing
    const handleCancel = () => {
        setEditMode(false);
        fetchProfile(); // Reset to original data
        setImagePreview(profileData.image);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 2, md: 3 } }}>
            {/* Success/Error Messages */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '6px', fontSize: '0.85rem' }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: '6px', fontSize: '0.85rem' }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left Column: Avatar & Quick Info */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: '8px',
                            textAlign: 'center',
                            border: '1px solid #E8E8E8',
                            bgcolor: '#FFFFFF',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                            <Avatar
                                src={imagePreview}
                                sx={{
                                    width: 140,
                                    height: 140,
                                    mx: 'auto',
                                    border: '1px solid #E8E8E8',
                                    boxShadow: 'none'
                                }}
                            />
                            {editMode && (
                                <label htmlFor="profile-image">
                                    <Input
                                        accept="image/*"
                                        id="profile-image"
                                        type="file"
                                        onChange={handleImageChange}
                                    />
                                    <IconButton
                                        component="span"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 5,
                                            right: 5,
                                            bgcolor: '#FFBD65',
                                            color: 'white',
                                            boxShadow: 'none',
                                            '&:hover': { bgcolor: '#e6a550' }
                                        }}
                                        size="small"
                                    >
                                        <PhotoCamera fontSize="small" />
                                    </IconButton>
                                </label>
                            )}
                        </Box>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#222', fontSize: '1.2rem' }}>
                            {profileData.name}
                        </Typography>
                        <Typography sx={{ color: '#666', mb: 2, fontSize: '0.85rem' }}>
                            {profileData.email}
                        </Typography>

                        <Box sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            bgcolor: 'rgba(255, 189, 101, 0.1)',
                            color: '#FFBD65',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            mb: 3
                        }}>
                            {profileData.role}
                        </Box>

                        <Divider sx={{ my: 3, borderColor: '#F0F0F0' }} />

                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#444', fontSize: '0.85rem' }}>
                                About
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, fontSize: '0.8rem' }}>
                                {profileData.description || 'No description provided.'}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column: Detailed Info & Form */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: '8px',
                            border: '1px solid #E8E8E8',
                            bgcolor: '#FFFFFF'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem' }}>
                                Personal Information
                            </Typography>
                            {!editMode ? (
                                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                    variant="outlined"
                                    startIcon={<Edit size={16} />}
                                    onClick={() => setEditMode(true)}
                                    sx={{
                                        borderRadius: '6px',
                                        textTransform: 'none',
                                        borderColor: '#FFBD65',
                                        color: '#FFBD65',
                                        '&:hover': { borderColor: '#e6a550', bgcolor: 'rgba(255, 189, 101, 0.05)' },
                                        fontSize: '0.85rem',
                                        px: 2.5
                                    }}
                                >
                                    Edit Profile
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                        variant="contained"
                                        startIcon={<Save size={16} />}
                                        onClick={handleSave}
                                        disabled={saving}
                                        sx={{
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            bgcolor: '#FFBD65',
                                            color: '#fff',
                                            fontSize: '0.85rem',
                                            px: 2.5
                                        }}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                        variant="text"
                                        onClick={handleCancel}
                                        disabled={saving}
                                        sx={{
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            color: '#666',
                                            fontSize: '0.85rem',
                                            px: 2.5
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Full Name"
                                    value={profileData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    disabled={!editMode}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Email Address"
                                    value={profileData.email}
                                    disabled
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                    helperText="Email cannot be changed"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Mobile Number"
                                    value={profileData.mobile}
                                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                                    disabled={!editMode}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Account Status"
                                    value={profileData.status}
                                    disabled
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Bio / Description"
                                    value={profileData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    disabled={!editMode}
                                    multiline
                                    rows={4}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Joined On"
                                    value={profileData.createdAt ? new Date(profileData.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                                    disabled
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Last Updated"
                                    value={profileData.updatedAt ? new Date(profileData.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) + ', ' + new Date(profileData.updatedAt).toLocaleDateString() : ''}
                                    disabled
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profile;
